import type { DataExtractionFieldName } from '~/entities/document'
import { CLAIM_PHASES, PRIOR_AUTH_PHASES } from '~/entities/document'

export const CONFIDENCE_SCORE_THRESHOLD = 0.95

export interface SubgroupField {
  key: DataExtractionFieldName
  customLabel?: string
  fullRow?: boolean
}

export interface Subgroup {
  title?: string
  titleIcon?: string
  description?: string
  fields: Array<SubgroupField>
  collapsable?: boolean
  hidden?: boolean
}

export interface Group {
  title?: string
  subgroups: Subgroup[]
  phases?: string[]
  excludePhases?: string[]
  main?: boolean
}

export const GROUPS: Group[] = [
  {
    title: 'Prior Auth Details',
    phases: PRIOR_AUTH_PHASES,
    main: true,
    subgroups: [
      {
        fields: [
          { key: 'authorization_code' },
          { key: 'appeal_id' },
          { key: 'reference_number' },
          { key: 'patient_account_number' },
          { key: 'auth_date_range_from' },
          { key: 'auth_date_range_to' },
          { key: 'peer_to_peer_indicator' },
          { key: 'decision' },
          { key: 'reason_for_denial', fullRow: true },
        ],
      },
    ],
  },
  {
    title: 'Claim Details',
    phases: CLAIM_PHASES,
    main: true,
    subgroups: [
      {
        fields: [
          { key: 'claim_number' },
          { key: 'claim_filing_date' },
          { key: 'claim_appeal_deadline' },
          { key: 'appeal_date_received' },
          { key: 'notification_date_provided' },
          { key: 'authorization_code' },
          { key: 'appeal_id' },
          { key: 'reference_number' },
          { key: 'patient_account_number' },
          { key: 'decision' },
          { key: 'reason_for_denial', fullRow: true },
        ],
      },
      {
        fields: [{ key: 'claim_filing_deadline' }, { key: 'deadline_to_resolve' }],
        collapsable: true,
      },
    ],
  },
  {
    title: 'Patient & Member Information',
    subgroups: [
      {
        title: 'Patient Details',
        fields: [
          { key: 'patient_name' },
          { key: 'patient_first_name' },
          { key: 'patient_last_name' },
          { key: 'patient_dob' },
        ],
      },
      {
        title: 'Member Details',
        fields: [{ key: 'member_name' }, { key: 'member_dob' }],
      },
      {
        title: 'Service Details',
        fields: [
          { key: 'service_date_from' },
          { key: 'total_amount' },
          { key: 'test_name' },
          { key: 'barcode' },
          { key: 'service_provider_number' },
          { key: 'service_provider_name' },
          { key: 'cpt_code' },
          { key: 'document_date' },
          { key: 'case_number' },
        ],
      },
    ],
  },
  {
    title: 'Payor Information',
    subgroups: [
      {
        title: 'Policy Details',
        fields: [{ key: 'insurance_company' }, { key: 'member_id' }, { key: 'patient_id' }],
      },
      {
        title: 'Contact Details',
        fields: [
          { key: 'contact_phone' },
          { key: 'contact_fax' },
          { key: 'contact_email' },
          { key: 'contact_website' },
        ],
        collapsable: true,
      },
      {
        title: 'Primary Mailing Address',
        fields: [{ key: 'contact_mail_address' }],
        collapsable: true,
      },
      {
        title: 'Courier Mailing Address',
        fields: [{ key: 'contact_courier_mail_address' }],
        collapsable: true,
      },
    ],
  },
  {
    title: 'Tagging',
    phases: CLAIM_PHASES,
    subgroups: [
      {
        title: 'Adjustment & Remark Codes',
        description: 'Details on Claim Adjustments and Remittance Remarks provided by the payer.',
        fields: [
          { key: 'carc_codes', fullRow: true },
          { key: 'rarc_codes', fullRow: true },
        ],
      },
      {
        title: 'Natera Tags',
        titleIcon: 'natera',
        description:
          'Select options that are relevant to the content in the document to improve routing and downstream workflows.',
        fields: [{ key: 'natera_tags', fullRow: true }],
      },
      {
        title: 'Comments',
        fields: [{ key: 'natera_tags_notes', fullRow: true }],
      },
    ],
  },
]
